import { toAbsoluteTechniquePhotoUrl } from "../../../../../services/techniquesPhotoServices";

export const STORAGE_KEY = "dashboard_techniques_quizz_state";

export const DEFAULT_QUIZZ_UI_STATE = {
  grade: "Toutes les ceintures",
  includeSelfDefense: false,
  count: 10,
};

export const FALLBACK_IMG = "/img/placeholder.jpg";

const BELT_BASE = [
  "blanche",
  "jaune",
  "orange",
  "verte",
  "bleue",
  "marron",
  "noire",
];

const collatorFR = new Intl.Collator("fr", {
  sensitivity: "base",
});

export function strip(value = "") {
  return String(value)
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .trim();
}

export function beltRank(ceinture = "") {
  const value = strip(ceinture);

  const danMatch = value.match(/(\d+)\s*(?:er|e)?\s*dan/);

  if (danMatch) {
    const dan = parseInt(danMatch[1], 10) || 1;
    return 6 + dan;
  }

  const foundColors = BELT_BASE.map((color, index) =>
    value.includes(color) ? index : -1
  ).filter((index) => index >= 0);

  if (foundColors.length === 1) return foundColors[0];

  if (foundColors.length >= 2) {
    return (
      foundColors.reduce((sum, index) => sum + index, 0) / foundColors.length
    );
  }

  const exactIndex = BELT_BASE.indexOf(value);

  if (exactIndex >= 0) return exactIndex;

  return Number.POSITIVE_INFINITY;
}

export function uniqSorted(values = []) {
  return Array.from(new Set(values.filter(Boolean))).sort((a, b) =>
    collatorFR.compare(String(a), String(b))
  );
}

export function buildDynamicOptions(techniques = []) {
  const grades = uniqSorted(
    techniques.map((technique) => technique?.ceinture).filter(Boolean)
  ).sort((a, b) => beltRank(a) - beltRank(b) || collatorFR.compare(a, b));

  return {
    grades: [DEFAULT_QUIZZ_UI_STATE.grade, ...grades],
    counts: [5, 10, 15, 20],
  };
}

export function getStoredQuizzState() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : {};

    return {
      ...DEFAULT_QUIZZ_UI_STATE,
      ...parsed,
    };
  } catch {
    return DEFAULT_QUIZZ_UI_STATE;
  }
}

export function shuffleArray(values = []) {
  const array = values.slice();

  for (let index = array.length - 1; index > 0; index -= 1) {
    const randomIndex = Math.floor(Math.random() * (index + 1));
    [array[index], array[randomIndex]] = [array[randomIndex], array[index]];
  }

  return array;
}

export function getTechniqueImage(technique) {
  const raw =
    technique?.image1_url ||
    technique?.image_url ||
    technique?.image ||
    technique?.photo_url ||
    "";

  return toAbsoluteTechniquePhotoUrl(raw) || FALLBACK_IMG;
}

export function buildQuestions(
  techniques = [],
  { grade = DEFAULT_QUIZZ_UI_STATE.grade, count = 10, wrongCount = 3 } = {}
) {
  if (!Array.isArray(techniques) || techniques.length === 0) return [];

  const pool =
    grade === DEFAULT_QUIZZ_UI_STATE.grade
      ? techniques
      : techniques.filter((technique) => technique?.ceinture === grade);

  const validPool = pool.filter(
    (technique) => technique?.id && technique?.nom && getTechniqueImage(technique)
  );

  if (validPool.length === 0) return [];

  const allNames = techniques
    .map((technique) => technique?.nom)
    .filter(Boolean);

  const seeds = shuffleArray(validPool).slice(
    0,
    Math.min(Number(count || 10), validPool.length)
  );

  return seeds.map((technique) => {
    const correctAnswer = technique.nom;

    const sameFamilyWrongAnswers = shuffleArray(
      techniques.filter(
        (item) =>
          item?.id !== technique.id &&
          item?.famille === technique.famille &&
          item?.nom
      )
    )
      .map((item) => item.nom)
      .filter((name) => name !== correctAnswer);

    let wrongAnswers = sameFamilyWrongAnswers.slice(0, wrongCount);

    if (wrongAnswers.length < wrongCount) {
      const otherAnswers = shuffleArray(
        allNames.filter(
          (name) => name !== correctAnswer && !wrongAnswers.includes(name)
        )
      );

      wrongAnswers = wrongAnswers.concat(
        otherAnswers.slice(0, wrongCount - wrongAnswers.length)
      );
    }

    const options = shuffleArray([correctAnswer, ...wrongAnswers]);

    return {
      id: technique.id,
      image: getTechniqueImage(technique),
      correctAnswer,
      options,
      famille: technique.famille || "",
      ceinture: technique.ceinture || "",
      traduction: technique.traduction || "",
    };
  });
}

export function calculatePercent(score, total) {
  if (!total) return 0;
  return Math.round((Number(score || 0) / Number(total || 1)) * 100);
}

export function getScoreMeta(score, total) {
  const percent = calculatePercent(score, total);

  if (percent >= 90) {
    return {
      label: "Excellent",
      message: "Très belle maîtrise des techniques.",
      className:
        "bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-200 dark:ring-emerald-800/60",
    };
  }

  if (percent >= 70) {
    return {
      label: "Solide",
      message: "Bon résultat, quelques techniques à revoir.",
      className:
        "bg-blue-50 text-blue-700 ring-blue-200 dark:bg-blue-900/20 dark:text-blue-200 dark:ring-blue-800/60",
    };
  }

  if (percent >= 50) {
    return {
      label: "En progression",
      message: "Continuez à vous entraîner régulièrement.",
      className:
        "bg-amber-50 text-amber-700 ring-amber-200 dark:bg-amber-900/20 dark:text-amber-200 dark:ring-amber-800/60",
    };
  }

  return {
    label: "À retravailler",
    message: "Reprenez les familles et les noms des techniques.",
    className:
      "bg-rose-50 text-rose-700 ring-rose-200 dark:bg-rose-900/20 dark:text-rose-200 dark:ring-rose-800/60",
  };
}